





















































































































































































import Vue, { PropType } from 'vue';
import store from '@/store';
import { OrgPeerGroup } from '@/models/orgStructure';
import * as Batch from '@/peerGroupBatch';
import { PeerGroupSort, loadPeerGroupSort } from '@/preferences';

import PeerGroupSortControl from '@/components/PeerGroupSortControl.vue';

type Tab = 'active' | 'archived';

// Archiving more than this many groups asks for confirmation first.
const CONFIRM_THRESHOLD = 5;

interface Row {
  peerGroup: OrgPeerGroup;
  selectable: boolean;
  selected: boolean;
  createdAtPretty: string;
}

const emptySelection = (): Record<Tab, number[]> => ({
  active: [],
  archived: []
});

export default Vue.extend({
  components: {
    PeerGroupSortControl
  },

  props: {
    peerGroups: Array as PropType<OrgPeerGroup[]>,
    value: Boolean
  },

  data() {
    return {
      tab: 'active' as Tab,
      query: '' as string | null,
      // Opens on the dashboard's saved sort.  Changes here stay in the dialog,
      // since the dashboard list only reads the preference when it mounts.
      sort: loadPeerGroupSort(),
      // Each tab keeps its own selection, so switching tabs loses nothing.
      selected: emptySelection(),
      confirming: false,
      working: false
    };
  },

  computed: {
    // Opened and closed by the parent through v-model.
    isOpen: {
      get(): boolean {
        return this.value;
      },
      set(open: boolean) {
        this.$emit('input', open);
      }
    },

    isArchiving(): boolean {
      return this.tab === 'active';
    },

    activeCount(): number {
      return this.peerGroups.filter(pg => pg.isActive).length;
    },

    archivedCount(): number {
      return this.peerGroups.filter(pg => !pg.isActive).length;
    },

    matchingGroups(): OrgPeerGroup[] {
      return Batch.matchGroups(
        this.peerGroups,
        this.isArchiving,
        this.query || '',
        this.sort
      );
    },

    tabSelection(): number[] {
      return this.selected[this.tab];
    },

    matching(): Row[] {
      return this.matchingGroups.map(peerGroup => ({
        peerGroup,
        selectable: Batch.isSelectable(peerGroup),
        selected: this.tabSelection.includes(peerGroup.peerGroupId),
        createdAtPretty: peerGroup.createdAt.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      }));
    },

    selectableCount(): number {
      return Batch.selectableIds(this.matchingGroups).length;
    },

    selectAll(): Batch.SelectAllState {
      return Batch.selectAllState(this.matchingGroups, this.tabSelection);
    },

    selectAllAria(): string {
      return { none: 'false', some: 'mixed', all: 'true' }[this.selectAll];
    },

    selectedCount(): number {
      return this.tabSelection.length;
    },

    hidden(): number {
      return Batch.hiddenCount(this.matchingGroups, this.tabSelection);
    },

    selectedSummary(): string {
      return this.selectedCount === 1
        ? '1 group selected'
        : `${this.selectedCount} groups selected`;
    },

    primaryLabel(): string {
      const verb = this.isArchiving ? 'Archive' : 'Restore';

      return `${verb} (${this.selectedCount})`;
    },

    primaryIcon(): string {
      return this.isArchiving ? 'mdi-archive' : 'mdi-package-up';
    },

    emptyText(): string {
      const hasStatus = this.isArchiving
        ? this.activeCount > 0
        : this.archivedCount > 0;

      if (!hasStatus) {
        return this.isArchiving
          ? 'No active groups found.'
          : 'No archived groups found.';
      }

      return `No groups match "${this.query}".`;
    }
  },

  methods: {
    setSort(sort: PeerGroupSort) {
      this.sort = sort;
    },

    setTab(tab: Tab) {
      this.tab = tab;
      this.confirming = false;
    },

    onToggleOne(row: Row) {
      if (!row.selectable || this.working) {
        return;
      }

      this.confirming = false;
      this.selected[this.tab] = Batch.toggleOne(
        this.tabSelection,
        row.peerGroup.peerGroupId
      );
    },

    onToggleAll() {
      if (this.selectableCount === 0 || this.working) {
        return;
      }

      this.confirming = false;
      this.selected[this.tab] = Batch.toggleAll(
        this.matchingGroups,
        this.tabSelection
      );
    },

    onPrimary() {
      if (
        this.isArchiving &&
        this.selectedCount > CONFIRM_THRESHOLD &&
        !this.confirming
      ) {
        this.confirming = true;
        return;
      }

      this.run();
    },

    async run() {
      const isActive = !this.isArchiving;
      const peerGroupIds = this.tabSelection.slice();

      this.working = true;

      try {
        const result = await store.dispatch.org.setPeerGroupsActive({
          peerGroupIds,
          isActive
        });

        this.$emit('done', { isActive, ...result });
      } finally {
        this.working = false;
      }

      this.close();
    },

    close() {
      this.isOpen = false;
    },

    reset() {
      this.tab = 'active';
      this.query = '';
      this.sort = loadPeerGroupSort();
      this.selected = emptySelection();
      this.confirming = false;
    }
  },

  watch: {
    isOpen(open: boolean) {
      if (open) {
        this.reset();
      }
    }
  }
});
